<template>
    <v-dialog v-model="visible" persistent max-width="560px">
        <v-card light>
            <v-card-title class="justify-center">
                <v-spacer/>
                <span class="text-h5">Novo Usuário</span>
                <v-spacer/>
            </v-card-title>

            <v-card-text>
                <v-row dense>
                    <v-col cols="12">
                        <v-text-field
                            dense
                            outlined
                            label="Nome"
                            v-model="form.name"
                            :disabled="loading"
                        />
                    </v-col>

                    <v-col cols="12">
                        <v-text-field
                            dense
                            outlined
                            type="email"
                            label="E-mail"
                            v-model="form.email"
                            :disabled="loading"
                        />
                    </v-col>

                    <v-col cols="12" sm="7">
                        <v-text-field
                            dense
                            outlined
                            label="Senha"
                            v-model="form.password"
                            :disabled="loading"
                            :type="mostrarSenha ? 'text' : 'password'"
                            :append-icon="mostrarSenha ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="mostrarSenha = !mostrarSenha"
                            hint="Mínimo de 8 caracteres, com pelo menos 1 letra e 1 número"
                            persistent-hint
                        />
                    </v-col>

                    <v-col cols="12" sm="5">
                        <v-select
                            dense
                            outlined
                            label="Tipo"
                            v-model="form.tipo"
                            :items="tiposDisponiveis"
                            :disabled="loading"
                        />
                    </v-col>

                    <v-col cols="12" sm="5">
                        <v-text-field
                            dense
                            outlined
                            label="Empresa ID"
                            v-model="form.empresa_id"
                            :disabled="loading"
                            :loading="buscandoEmpresa"
                            @input="onEmpresaIdInput"
                        />
                    </v-col>

                    <v-col cols="12" sm="7" class="d-flex align-center">
                        <span
                            class="text-caption"
                            :class="empresaNome ? 'success--text' : 'error--text'"
                        >
                            {{ empresaNome || empresaErro }}
                        </span>
                    </v-col>
                </v-row>

                <v-alert dense text type="info" class="mb-0 mt-2 text-caption">
                    O usuário entra sem nenhuma permissão. Depois de salvar, a tela de
                    permissões abre para você liberar o que ele pode ver.
                </v-alert>

                <v-alert
                    v-if="criandoForaDaSuaEmpresa"
                    dense
                    text
                    type="warning"
                    class="mb-0 mt-2 text-caption"
                >
                    Esta empresa não é a sua: o usuário vai entrar no painel de
                    {{ empresaNome || `empresa ${form.empresa_id}` }}.
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <v-btn text color="warning" :disabled="loading" @click="$emit('close')">Cancelar</v-btn>
                <v-spacer/>
                <v-btn color="success" :loading="loading" @click="salvar">
                    <v-icon>mdi-content-save</v-icon>&nbsp;Salvar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'UsuarioDialog',

    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        form: {
            name: '',
            email: '',
            password: '',
            tipo: 'gestor',
            empresa_id: '',
        },
        loading: false,
        mostrarSenha: false,
        empresaNome: '',
        empresaErro: '',
        buscandoEmpresa: false,
        buscaEmpresaTimer: null,
    }),

    computed: {
        tiposDisponiveis() {
            const tipos = [
                { text: 'Cliente', value: 'cliente' },
                { text: 'Consumidor', value: 'consumidor' },
                { text: 'Gestor', value: 'gestor' },
                { text: 'Suporte', value: 'suporte' },
            ];

            if (this.isDiretor()) {
                tipos.push({ text: 'Diretor', value: 'diretor' });
            }

            return tipos;
        },

        criandoForaDaSuaEmpresa() {
            const minha = String(this.$store.state.usuario.empresa_id || '');
            const escolhida = String(this.form.empresa_id || '');

            return escolhida !== '' && minha !== '' && escolhida !== minha;
        },
    },

    watch: {
        visible(aberto) {
            if (aberto) {
                this.form = {
                    name: '',
                    email: '',
                    password: '',
                    tipo: 'gestor',
                    empresa_id: String(this.$store.state.usuario.empresa_id || ''),
                };
                this.mostrarSenha = false;
                this.empresaNome = '';
                this.empresaErro = '';
                this.buscarEmpresa();
            }
        },
    },

    methods: {
        onEmpresaIdInput() {
            this.empresaNome = '';
            this.empresaErro = '';

            clearTimeout(this.buscaEmpresaTimer);
            this.buscaEmpresaTimer = setTimeout(this.buscarEmpresa, 400);
        },

        async buscarEmpresa() {
            const id = String(this.form.empresa_id || '').trim();

            if (!id) {
                this.empresaErro = 'Informe a empresa';
                return;
            }

            this.buscandoEmpresa = true;

            try {
                const { data } = await this.$http.get(`usuarios/empresa/${id}`);

                if (data.type === 'success') {
                    this.empresaNome = data.data.fantasia;
                    this.empresaErro = '';
                } else {
                    this.empresaNome = '';
                    this.empresaErro = data.msg;
                }
            } catch (e) {
                this.empresaNome = '';
                this.empresaErro = 'Não foi possível conferir a empresa';
            } finally {
                this.buscandoEmpresa = false;
            }
        },

        senhaValida(senha) {
            return typeof senha === 'string'
                && senha.length >= 8
                && /[a-zA-ZÀ-ÿ]/.test(senha)
                && /\d/.test(senha);
        },

        validar() {
            if (!this.form.name.trim()) {
                return 'Informe o nome';
            }

            if (!/^\S+@\S+\.\S+$/.test(this.form.email.trim())) {
                return 'Informe um e-mail válido';
            }

            if (!this.senhaValida(this.form.password)) {
                return 'A senha deve ter no mínimo 8 caracteres, com pelo menos 1 letra e 1 número';
            }

            if (!String(this.form.empresa_id || '').trim()) {
                return 'Informe a empresa';
            }

            if (!this.empresaNome) {
                return this.empresaErro || 'Confira o Empresa ID';
            }

            return null;
        },

        async salvar() {
            const erro = this.validar();

            if (erro) {
                this.notify(erro, 'warning');
                return;
            }

            if (this.criandoForaDaSuaEmpresa) {
                const confirmado = await this.$root.$confirm(
                    'Confirmar empresa',
                    `${this.form.name.trim()} vai ser criado em "${this.empresaNome}" `
                        + `(empresa ${this.form.empresa_id}), que não é a sua. Confirma?`,
                );

                if (!confirmado) {
                    return;
                }
            }

            this.loading = true;

            try {
                const { data } = await this.$http.post('usuarios/control', {
                    name: this.form.name.trim(),
                    email: this.form.email.trim(),
                    password: this.form.password,
                    tipo: this.form.tipo,
                    empresa_id: String(this.form.empresa_id).trim(),
                });

                this.notify(data.msg, data.type);

                if (data.type === 'success') {
                    this.$emit('onCadastro', data.data);
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
